import { motion, useReducedMotion } from 'framer-motion';
import { Check, Trophy, Sparkles, Flame } from 'lucide-react';
import { useState } from 'react';

import {
  TIER_META,
  TIER_RANK,
  STREAK_DIAMANTE_DE_CONSTANCIA_MIN,
  type TierName,
  type ProgressSummary,
} from '@/features/agent-progress/tierConstants';
import { TierBadge } from './TierBadge';
import { ProgressRing } from './ProgressRing';
import { useT } from '@/i18n';

const WEEKDAY_ABBR_ES = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

function isoDow(nyDate: string): number {
  const d = new Date(nyDate + 'T12:00:00Z');
  return ((d.getUTCDay() + 6) % 7) + 1;
}

export function SectionToday({ data }: { data: ProgressSummary }) {
  const t = useT();
  const tier = data.monthly.tierDisplayed;
  const meta = TIER_META[tier];
  const todayPct = Math.min(100, (data.today.screeningsToday / data.today.targetToday) * 100);
  const todayPending = data.pendingValidation.todayCount;
  const todaySubmittedTotal = data.today.screeningsToday + todayPending;

  return (
    <section className="p-6">
      <h3 className="text-sm font-medium text-nav-navy mb-4">{t('progress.section.today')}</h3>
      <div className="flex items-center gap-6">
        <ProgressRing pct={todayPct} size={120} stroke={10} colorClass={meta.textClass}>
          <div className="text-center">
            <div className="text-2xl font-semibold tabular-nums text-nav-navy">
              {data.today.screeningsToday}
              <span className="text-base text-nav-slate">/{data.today.targetToday}</span>
            </div>
            {data.today.hitTarget ? (
              <Check className="w-4 h-4 mx-auto text-nav-sageMid" />
            ) : (
              <span className="text-xs text-nav-slate">{t('progress.toTargetToday', { count: data.today.targetToday - data.today.screeningsToday })}</span>
            )}
          </div>
        </ProgressRing>
        <div className="flex-1">
          <div className="text-xs text-nav-slate mb-1">{t('progress.tierThisMonth')}</div>
          <TierBadge tier={tier} size="lg" />
          {todayPending > 0 && (
            <div className="mt-2 space-y-0.5">
              <div className="text-xs text-nav-navy">
                {t('progress.today.validatedNote', { validated: data.today.screeningsToday, submitted: todaySubmittedTotal })}
              </div>
              <div className="text-xs text-nav-amberText dark:text-nav-amber font-medium">
                {t('progress.pendingValidation.badge', { count: todayPending })}
              </div>
            </div>
          )}
          {data.monthly.nextTier && (
            <div className="mt-3 text-xs text-nav-slate">
              {t('progress.toNextTier', { count: data.monthly.screeningsToNextTier ?? 0 })}
              <div className="mt-1 h-1.5 bg-nav-hair rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-current"
                  initial={{ width: 0 }}
                  animate={{ width: `${data.monthly.progressPctToNextTier}%` }}
                  transition={{ duration: 0.7, ease: 'easeOut' }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export function SectionTierJourney({ data }: { data: ProgressSummary }) {
  const t = useT();
  const reduce = useReducedMotion();
  const rows = [...data.history12].reverse();
  const maxScreenings = Math.max(800, ...rows.map((r) => r.totalScreenings));

  return (
    <section className="p-6">
      <h3 className="text-sm font-medium text-nav-navy mb-4">{t('progress.section.tierJourney')}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-nav-slate">{t('progress.tierJourney.empty')}</p>
      ) : (
        <div className="flex items-end gap-2 overflow-x-auto pb-2" style={{ minHeight: 120 }}>
          {rows.map((row, idx) => {
            const heightPct = Math.max(4, (row.totalScreenings / maxScreenings) * 100);
            const meta = TIER_META[row.tierReached];
            return (
              <div key={`${row.periodYear}-${row.periodMonth}`} className="flex flex-col items-center gap-1 flex-shrink-0 min-w-[44px]">
                <div className="text-[10px] text-nav-slate tabular-nums">{row.totalScreenings}</div>
                <motion.div
                  className={`w-8 rounded-t-md ${meta.bgClass} ring-1 ${meta.ringClass}`}
                  initial={reduce ? { height: heightPct + '%' } : { height: 0 }}
                  animate={{ height: heightPct + '%' }}
                  transition={{ duration: 0.5, delay: idx * 0.04, ease: 'easeOut' }}
                  style={{ minHeight: 8 }}
                  title={`${row.periodYear}-${String(row.periodMonth).padStart(2, '0')}: ${row.totalScreenings}`}
                />
                <div className="text-[10px] text-nav-slate tabular-nums">{String(row.periodMonth).padStart(2, '0')}/{String(row.periodYear).slice(2)}</div>
              </div>
            );
          })}
        </div>
      )}
      {data.pendingValidation.monthCount > 0 && (
        <p className="mt-3 text-xs text-nav-amberText dark:text-nav-amber">
          {t('progress.tierJourney.pendingHint', { count: data.pendingValidation.monthCount })}
        </p>
      )}
    </section>
  );
}

export function SectionAchievements({ data }: { data: ProgressSummary }) {
  const t = useT();
  const ach = data.achievements;
  const bestTierEverRank = data.history12.reduce((max, r) => Math.max(max, TIER_RANK[r.tierReached]), 0);

  const badges = [
    {
      key: 'caminoBronce',
      unlocked: data.monthly.screeningsMonth > 0 || ach.monthsAtBronceOrAbove > 0,
      tierKey: 'tier.caminoBronce',
      tier: 'below_floor' as TierName,
    },
    { key: 'bronce', unlocked: bestTierEverRank >= TIER_RANK.base, tierKey: 'tier.bronce', tier: 'base' as TierName },
    { key: 'plata',  unlocked: bestTierEverRank >= TIER_RANK.t1,   tierKey: 'tier.plata',  tier: 't1'   as TierName },
    { key: 'oro',    unlocked: bestTierEverRank >= TIER_RANK.t2,   tierKey: 'tier.oro',    tier: 't2'   as TierName },
    { key: 'diamante', unlocked: bestTierEverRank >= TIER_RANK.t3, tierKey: 'tier.diamante', tier: 't3' as TierName },
  ];

  return (
    <section className="p-6">
      <h3 className="text-sm font-medium text-nav-navy mb-4">{t('progress.section.achievements')}</h3>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
        {badges.map((b) => {
          const meta = TIER_META[b.tier];
          const Icon = meta.icon;
          return (
            <div
              key={b.key}
              className={`flex flex-col items-center p-3 rounded-lg border ${b.unlocked ? `${meta.bgClass} ${meta.textClass} border-transparent ring-1 ${meta.ringClass}` : 'bg-nav-softer text-nav-faint border-nav-card grayscale opacity-60'}`}
            >
              <Icon className="w-6 h-6" aria-hidden />
              <span className="text-xs mt-1 font-medium text-center">{t(b.tierKey as never)}</span>
            </div>
          );
        })}
        <div
          className={`flex flex-col items-center p-3 rounded-lg border col-span-3 sm:col-span-2 ${ach.diamanteDeConstancia ? 'bg-nav-tealTint text-nav-tealDark border-transparent ring-1 ring-nav-teal' : 'bg-nav-softer text-nav-faint border-nav-card grayscale opacity-60'}`}
          title={t('streak.badge.tooltip', { count: STREAK_DIAMANTE_DE_CONSTANCIA_MIN })}
        >
          <Sparkles className="w-6 h-6" aria-hidden />
          <span className="text-xs mt-1 font-medium text-center">{t('streak.badge.diamanteDeConstancia')}</span>
        </div>
        {ach.bestMonth && (
          <div className="flex flex-col items-center p-3 rounded-lg border bg-nav-amberTint text-nav-amberText border-nav-amber/40 col-span-3 sm:col-span-3">
            <Trophy className="w-6 h-6" aria-hidden />
            <span className="text-xs mt-1 text-center">
              {t('progress.bestMonth', { count: ach.bestMonth.totalScreenings })}
            </span>
            <span className="text-[10px] tabular-nums">
              {String(ach.bestMonth.periodMonth).padStart(2, '0')}/{ach.bestMonth.periodYear}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

// ============================================================
// SectionLast7Days — Fase 4.5b visual upgrade (interactive bars)
// ============================================================

type BarStyle = {
  fill: string;
  glow: boolean;
  superEffort: boolean;
};

function barStyleForCount(count: number, isWorkingDay: boolean): BarStyle {
  if (!isWorkingDay) return { fill: 'bg-nav-hair dark:bg-nav-slate', glow: false, superEffort: false };
  if (count === 0)   return { fill: 'bg-nav-hair dark:bg-nav-slate', glow: false, superEffort: false };
  if (count >= 30)   return { fill: 'bg-nav-amber dark:bg-nav-amber', glow: true,  superEffort: true  };
  if (count >= 25)   return { fill: 'bg-nav-sageMid dark:bg-nav-sageText', glow: true,  superEffort: false };
  if (count >= 20)   return { fill: 'bg-nav-sageMid dark:bg-nav-sageMid', glow: false, superEffort: false };
  if (count >= 10)   return { fill: 'bg-nav-sage dark:bg-nav-sageMid', glow: false, superEffort: false };
  return                    { fill: 'bg-nav-amber dark:bg-nav-amber', glow: false, superEffort: false };
}

function barHeightPct(count: number, target = 25): number {
  if (count === 0) return 3;
  const ratio = count / target;
  if (ratio >= 1) return 100;
  if (ratio >= 0.8) return 60 + (ratio - 0.8) * 200;
  if (ratio >= 0.4) return 30 + (ratio - 0.4) * 75;
  return Math.max(8, ratio * 75);
}

export function SectionLast7Days({ data }: { data: ProgressSummary }) {
  const t = useT();
  const reduce = useReducedMotion();
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const today = data.today.nyDate;
  const target = data.today.targetToday;
  const avg = data.last7Days.length === 0
    ? 0
    : Math.round(data.last7Days.reduce((s, d) => s + d.screenings, 0) / data.last7Days.length);

  return (
    <section className="p-6">
      <h3 className="text-sm font-medium text-nav-navy mb-4">{t('progress.section.last7Days')}</h3>
      <div className="relative flex items-end justify-between gap-3 px-1 select-none" style={{ height: 180 }}>
        {/* Average line */}
        {avg > 0 && (
          <div
            className="absolute left-0 right-0 border-t border-dashed border-nav-faint pointer-events-none"
            style={{ bottom: `${barHeightPct(avg, target)}%` }}
            aria-hidden
          >
            <span className="absolute -top-3.5 right-0 text-[10px] text-nav-slate bg-white dark:bg-nav-navy px-1">
              {t('progress.last7.avg', { count: avg })}
            </span>
          </div>
        )}

        {data.last7Days.map((d, idx) => {
          const dow = isoDow(d.nyDate);
          const dowLabel = WEEKDAY_ABBR_ES[dow - 1] ?? '?';
          const isToday = d.nyDate === today;
          const style = barStyleForCount(d.screenings, d.isWorkingDay);
          const height = barHeightPct(d.screenings, target);
          const opacityClass = d.isWorkingDay ? 'opacity-100' : 'opacity-60';
          const hovering = hoverIdx === idx;
          return (
            <div
              key={d.nyDate}
              className={`relative flex flex-col items-center flex-1 ${opacityClass}`}
              onMouseEnter={() => setHoverIdx(idx)}
              onMouseLeave={() => setHoverIdx(null)}
              onFocus={() => setHoverIdx(idx)}
              onBlur={() => setHoverIdx(null)}
              tabIndex={0}
            >
              <div
                className={`text-[11px] tabular-nums mb-1 ${isToday ? 'text-nav-tealDark dark:text-nav-teal font-bold' : 'text-nav-navy'}`}
                aria-hidden
              >
                {d.screenings}
              </div>
              <div className="relative w-full max-w-[36px] mx-auto" style={{ height: '70%' }}>
                {style.superEffort && (
                  <Sparkles className="absolute -top-3 left-1/2 -translate-x-1/2 w-3 h-3 text-nav-amber" aria-hidden />
                )}
                <motion.div
                  className={`absolute bottom-0 left-0 right-0 rounded-t-md ${style.fill} ${style.glow ? 'shadow-md shadow-nav-amber/30' : ''} ${isToday ? 'ring-2 ring-nav-teal dark:ring-nav-tealDark' : ''} ${hovering ? 'scale-y-105 brightness-110' : ''} transition-all`}
                  initial={reduce ? { height: height + '%' } : { height: 0 }}
                  animate={{ height: height + '%' }}
                  transition={{ duration: 0.55, delay: reduce ? 0 : idx * 0.06, type: 'spring', stiffness: 220, damping: 20 }}
                  aria-label={t('progress.last7.barAria', { dow: dowLabel, count: d.screenings })}
                />
                {isToday && !reduce && (
                  <motion.div
                    className="absolute inset-0 rounded-t-md ring-2 ring-nav-teal/60 pointer-events-none"
                    animate={{ opacity: [0.3, 0.7, 0.3] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                    aria-hidden
                  />
                )}
              </div>
              <div className={`text-[10px] mt-1 ${isToday ? 'text-nav-tealDark dark:text-nav-teal font-semibold' : d.isWorkingDay ? 'text-nav-navy' : 'text-nav-slate'}`}>
                {dowLabel}
              </div>

              {hovering && (
                <div
                  role="tooltip"
                  className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap rounded-lg bg-nav-navy text-white text-[11px] px-2 py-1.5 shadow-lg pointer-events-none"
                >
                  <div className="font-medium">{d.nyDate}</div>
                  <div>{t('progress.last7.tooltipCount', { count: d.screenings })}</div>
                  <div className="text-nav-faint">{t('progress.last7.tooltipTarget', { count: target })}</div>
                  {d.isWorkingDay && (
                    d.hitTarget
                      ? <div className="text-nav-sageMid">✓ {t('progress.last7.tooltipMet')}</div>
                      : <div className="text-nav-amber">{t('progress.last7.tooltipMissing', { count: Math.max(0, target - d.screenings) })}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {data.streak.daysWorking >= 3 && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-nav-amberTint dark:bg-nav-amberTint text-nav-amberText dark:text-nav-amberText border border-nav-amber/40 dark:border-nav-amber/40">
          <Flame className="w-3.5 h-3.5 fill-nav-amber" aria-hidden />
          <span className="font-semibold tabular-nums">{data.streak.daysWorking}</span>
          <span className="text-nav-amberText/80">{t('streak.daysWorking', { count: data.streak.daysWorking })}</span>
        </div>
      )}
    </section>
  );
}
